import * as RadixMenu from '@radix-ui/react-dropdown-menu'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/app/routes'
import { shellStrings as s } from '@/components/navigation/strings'
import { useUser } from '@/features/auth/useSession'
import { signOutEverywhere } from '@/features/auth/auth-flow'
import { env } from '@/config/env'
import { cn, mediaUrl } from '@/lib/utils'
import { fullName } from '@/types/models'
import { DsAvatar, DsIconGlyph, DsIcons, DsOverlay, type DsIcon } from '@/ds'
import { useHasVerification } from '@/features/capabilities/useCapabilities'

export function AccountMenu() {
  const user = useUser()
  const navigate = useNavigate()
  // Hidden where the country offers neither verification nor payouts. Above the guest guard
  // because a hook cannot be called after an early return.
  const hasVerification = useHasVerification()
  if (!user) return null
  const logout = async () => {
    const ok = await DsOverlay.confirm({ title: s.logoutConfirmTitle, message: s.logoutConfirmMessage, confirmLabel: s.logout, cancelLabel: s.cancel, destructive: true })
    if (ok) await signOutEverywhere((p) => navigate(p, { replace: true }))
  }
  const item = (label: string, icon: DsIcon, onSelect: () => void, destructive = false) => (
    <RadixMenu.Item
      onSelect={onSelect}
      className={cn('flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-small outline-none data-[highlighted]:bg-muted', destructive ? 'text-danger' : 'text-foreground')}
    >
      <DsIconGlyph icon={icon} size={16} />
      {label}
    </RadixMenu.Item>
  )
  return (
    <RadixMenu.Root>
      <RadixMenu.Trigger aria-label={s.accountMenu} className="inline-flex size-10 items-center justify-center rounded-full outline-none hover:bg-muted focus-visible:ring-focus">
        <DsAvatar name={fullName(user)} imageUrl={mediaUrl(user.avatar, env.mediaHost)} size="sm" />
      </RadixMenu.Trigger>
      <RadixMenu.Portal>
        <RadixMenu.Content align="end" sideOffset={8} className="z-50 w-64 rounded-xl border bg-popover p-1 text-popover-foreground shadow-lg animate-ds-scale-in">
          <div className="px-3 py-2">
            <div className="truncate text-small-strong text-foreground">{fullName(user)}</div>
            <div className="truncate text-caption text-muted-foreground">{user.email || user.mobile}</div>
          </div>
          <RadixMenu.Separator className="my-1 h-px bg-border" />
          {item(s.settings, DsIcons.settings, () => navigate(routes.account))}
          {item(s.notificationSettings, DsIcons.bell, () => navigate(routes.accountNotifications))}
          {hasVerification && item(s.kyc, DsIcons.idCard, () => navigate(routes.kyc))}
          {item(s.help, DsIcons.chat, () => navigate(routes.support))}
          <RadixMenu.Separator className="my-1 h-px bg-border" />
          {item(s.logout, DsIcons.logout, () => void logout(), true)}
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  )
}
